import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { EFFilterData, EFProduct } from '../../../models/experience.model';
import { LocalizationService } from '../../../services/localization.service';
import { PGUtilities } from '../../../utilities/pg-utilities';
import { EnvironmentService } from '../../../services/environment.service';
import { EFDataService } from '../../../services/ef-data.service';
import { DataService } from '../../../services/data.service';

@Component({
    selector: 'app-ef-showcase',
    templateUrl: './ef-showcase.component.html',
    styleUrls: ['./ef-showcase.component.scss'],
    standalone: false
})
export class EfShowcaseComponent implements OnInit, OnDestroy {

    @Input() showQR:string;

    @Input() showcaseContent:Array<{ type: string, element: string }>
    @Input() productsFilter:EFFilterData;
    @Input() realmId:string;

    
    showcaseResourceIdList:Array<{ resource: string, id: string }> = null;
    private _showcaseProductDataIndex:{ [id:string]: { time: number, data: EFProduct } } = null;

    showcaseLanguageIndex:number = 0;

    previousShowcaseIndex:number = null;
    currentShowcaseIndex:number = null;
    nextShowcaseIndex:number = null;

    switchLanguage = false;

    QROptions = null;

    constructor(private localizationService:LocalizationService, private environmentService:EnvironmentService, private efDataService:EFDataService, private dataService:DataService) { }

    private _showcaseInterval = null;
    private _showcaseTimeout = null;

    private _dataDuration = 5 * 60 * 1000;

    isLoading = false;

    private _checkQRScale:() => void;

    ngOnInit(): void {
        this._checkQRScale = () => {
            let _pixels = 45;
            let _scale = Math.max(2, Math.min(4, Math.floor(window.innerHeight / 3 / _pixels)))

            this.QROptions = {
                scale: _scale,
                width: _scale * _pixels,
            }
        }

        window.addEventListener('resize', this._checkQRScale)

        this._checkQRScale();
        this._loadData()
    }

    private _lastResourceIdListCheck:number = null;
    private _resourceIdListDuration = 5 * 60 * 1000;

    private _loadData() {
        this.isLoading = true;

        this.showcaseResourceIdList = []
        this._showcaseProductDataIndex = {};

        this._checkShowQRTotem().then(() => {
            if(this.showcaseContent != null && this.showcaseContent.length > 0) {
                for(let _item of this.showcaseContent) {
                    this.showcaseResourceIdList.push({
                        resource: this.efDataService.getResourceFromProductType(_item.type, true),
                        id: _item.element
                    })
                }    
                
                this._afterLoadData()
            }
            else {
                this._getShowcaseResourceIdList().then((data) => {
                    this.showcaseResourceIdList = data
                    this._afterLoadData()
                })
            }
        })
    }

    private _totemURL = null;

    private async _checkShowQRTotem() {
        this._totemURL = null;

        if(this.showQR && /^TotemEnvironment/.test(this.showQR)) {
            let _totemPID = this.showQR.split('_')[1]

            this.dataService.getResourceData('TotemEnvironment', { filter: [
                { field: 'pid', operator: '==', value: [ _totemPID ] }
            ] }, null, null, 'public').subscribe((data) => {
                let _alterantiveURL = data[0].alternative_url

                if(_alterantiveURL) {
                    this._totemURL = _alterantiveURL + '/' + _totemPID
                }
                else {
                    this._totemURL = this.environmentService.getApplicationURL('Totem') + '/' + _totemPID
                }
            })
        }
    }

    private async _getShowcaseResourceIdList() {
        this._lastResourceIdListCheck = Date.now()
        this.efDataService.resourcesCacheReset()

        let _result:Array<{ resource: string, id: string }> = []

        for(let _item of ['experience~Event']) {
            let _data = await this._loadAllResourcePages(_item);

            for(let _product of _data) {
                if(_product.cover || _product.flyer || (_product.images != null && _product.images.length > 0)) {
                    let _isIn = true;

                    if(this.environmentService.environment.ExperienceFinderShowcaseTag) {
                        let _filterTag = this.environmentService.environment.ExperienceFinderShowcaseTag.toLowerCase().replace(/^\s*/, '').replace(/\s*$/, '')

                        _isIn = false;

                        if(_product.tags) {
                            if(_product.tags.indexOf(_filterTag) != -1) _isIn = true
                        }
                    }

                    if(_isIn) {
                        let _idSplit = _product.id.split('_')
                        _result.push({
                            resource: _idSplit[0],
                            id: _idSplit[1]
                        })
                    }
                }
            }
        }

        return _result;
    }

    private async _loadAllResourcePages(resource:string) {
        let _result:Array<EFProduct> = []

        await new Promise<void>((resolve, reject) => {
            this.efDataService.listResourceProducts(resource, this.localizationService.currentLanguage, null, null, this.realmId).subscribe((data) => {
                data = this.efDataService.applyProductsFilter(data, this.productsFilter)

                for(let _item of data) {
                    if(!_item.isPastProduct()) {
                        _result.push(_item)
                    }
                }

                resolve()
            })
        })

        return _result;
    }

    private _afterLoadData() {
        PGUtilities.shuffleArray(this.showcaseResourceIdList)

        this.showcaseLanguageIndex = 0;

        this.previousShowcaseIndex = null;
        this.currentShowcaseIndex = null;
        this.nextShowcaseIndex = null;

        if(this.showcaseResourceIdList.length == 0) {
            this.isLoading = false;
            this._showcaseInterval = setInterval(() => {
                this._checkResourceIdListReload()
            }, 5000)
        }
        else {
            this.nextShowcaseIndex = 0;
            this._checkLoadNextData()

            this._showcaseTimeout = setTimeout(() => {
                this._showcaseTimeout = null;

                this.currentShowcaseIndex = 0;
                this.nextShowcaseIndex = (this.nextShowcaseIndex + 1) % this.showcaseResourceIdList.length;
                this._checkLoadNextData()
                
                this.isLoading = false;

                this._showcaseInterval = setInterval(() => {
                    let _nextShowcaseLanguageIndex
                    _nextShowcaseLanguageIndex = (this.showcaseLanguageIndex + 1) % this.localizationService.availableApplicationLanguages.length;

                    if(_nextShowcaseLanguageIndex == 0) {
                        this.previousShowcaseIndex = this.currentShowcaseIndex;
                        this.currentShowcaseIndex = null;

                        this._showcaseTimeout = setTimeout(() => {
                            this._showcaseTimeout = null;
                            this.showcaseLanguageIndex = _nextShowcaseLanguageIndex;

                            this.currentShowcaseIndex = this.nextShowcaseIndex;
                            this.nextShowcaseIndex = (this.nextShowcaseIndex + 1) % this.showcaseResourceIdList.length;
                            this._checkLoadNextData()
                            this._checkResourceIdListReload()
                        }, 1000)
                    }
                    else {
                        this.switchLanguage = true;

                        this._showcaseTimeout = setTimeout(() => {
                            this._showcaseTimeout = null;
                            this.showcaseLanguageIndex = _nextShowcaseLanguageIndex;

                            this.switchLanguage = false;
                        }, 250)
                    }
                }, 5000)
            }, 1000)
        }
    }

    private _checkLoadNextData() {
        let _index = this.nextShowcaseIndex
        let _nextResourceId = this.showcaseResourceIdList[_index]
        let _id = this.getProductDataId(_nextResourceId)

        if(this._showcaseProductDataIndex[_id] == null || (this._showcaseProductDataIndex[_id].time + this._dataDuration < Date.now())) {
            this.efDataService.getResourceProduct(_nextResourceId.resource, _nextResourceId.id).subscribe((data) => {
                this._showcaseProductDataIndex[_id] = { time: Date.now(), data: data }
            })
        }
    }

    private async _checkResourceIdListReload() {
        if(this._lastResourceIdListCheck != null && Date.now() > this._lastResourceIdListCheck + this._resourceIdListDuration) {
            let _list = await this._getShowcaseResourceIdList()

            let _isDifferent = _list.length != this.showcaseResourceIdList.length;

            if(!_isDifferent) {
                for(let _check of this.showcaseResourceIdList) {
                    let _isIn = false;

                    for(let _item of _list) {
                        if(_item.resource == _check.resource && _item.id == _check.id) {
                            _isIn = true;
                            break;
                        }
                    }

                    if(!_isIn) {
                        _isDifferent = true;
                        break;
                    }
                }
            }

            if(_isDifferent) {
                if(this._showcaseInterval != null) clearInterval(this._showcaseInterval)
                if(this._showcaseTimeout != null) clearTimeout(this._showcaseTimeout)

                this._loadData();
            }
        }
    }

    getProductDataId(item:{ resource: string, id: string }) {
        return item.resource + '_' + item.id
    }

    getProductData(item:{ resource: string, id: string }) {
        return this._showcaseProductDataIndex[this.getProductDataId(item)];
    }

    ngOnDestroy() {
        window.removeEventListener('resize', this._checkQRScale)

        if(this._showcaseInterval != null) clearInterval(this._showcaseInterval)
        if(this._showcaseTimeout != null) clearTimeout(this._showcaseTimeout)
    }

    getTranslatedProperty(product:EFProduct, prop:string) {
        let _language = this.localizationService.availableApplicationLanguages[this.showcaseLanguageIndex]

        if(product.translations != null && product.translations[_language] != null && product.translations[_language][prop] != null) {
            return product.translations[_language][prop]
        }
        else if(product[prop] != null) {
            return product[prop]
        }
    }

    formatProductLocation(product:EFProduct) {
        let _retVal = '';
        
        if(product.city != null) _retVal += product.city;
        if(product.province != null) {
            if(_retVal != '') _retVal += ' '
            _retVal += '(' + product.province + ')';
        }
        if(product.address != null) {
            if(_retVal != '') _retVal += ', '
            _retVal += product.address;
        }

        return _retVal
    }

    getProductURL(product:EFProduct) {
        if(this.showQR) {
            if(/^TotemEnvironment/.test(this.showQR)) {
                return this._totemURL + '#/product/' + product.id
            }
            else {
                return this.environmentService.getApplicationURL('ExperienceFinder') + '/product/' + product.id.replace('_', '/')
            }
        }
    }

    getCurrentLanguage() {
        return this.localizationService.availableApplicationLanguages[this.showcaseLanguageIndex].split('_')[0].toUpperCase()
    }

    showTextOn(product:EFProduct) {
        return !product.flyer
    }

    showQROn(product:EFProduct) {
        return this.showQR && (!this.environmentService.environment.ExperienceFinderShowcaseHideQROnFlyer || !product.flyer)
    }
}
